import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { FunctionType, OnboardingStepKey } from '@cbgms/api'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import OnboardingAddMechanicsForm from './OnboardingAddMechanicsForm'
import { createEmployee, inviteEmployee } from 'app/settings/state/Employees/actions'
import { useDispatch } from 'react-redux'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { NewEmployee } from './types.d'
import { AddMechanicStepCTA } from './AddMechanicStepCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'
import { Subscriptions } from '@carsys/enums/subscription'

interface StepInputProps {
  basePath: string
  prefilledEmployee?: NewEmployee
}

interface StepProps {
  basePath: string
  onSubmit: (employees: NewEmployee[]) => void
  prefilledEmployee?: NewEmployee
  modules: Subscriptions[]
}

export const useAddMechanicStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, prefilledEmployee, onboardingInfo }) => {
  const [newEmployees, setNewEmployees] = useState<NewEmployee[]>([])
  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.Mechanic
  const stepData = useStepData(stepName, onboardingInfo, newEmployees?.length ? { Mechanics: newEmployees } : undefined)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const hideSkip = useDefaultSkipSetting(onboardingInfo)
  const onStepSubmit = useCallback(
    (values: NewEmployee[]) => {
      setNewEmployees(values)
    },
    [setNewEmployees]
  )

  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    for (const emp of newEmployees) {
      try {
        // create each employee and invite them
        const { Data } = await dispatch(createEmployee({ ...emp, Functions: [FunctionType.Mechanic] }))
        await dispatch(
          inviteEmployee(Data.UUID, {
            EmailAddress: emp.EmailAddressWork
          })
        )
      } catch {
        // ignore error, notification shown in global handler
      }
    }
  }, [newEmployees, dispatch])

  return {
    Component: AddMechanicsStep,
    CTAComponent: AddMechanicStepCTA,
    StepperText: onboardingInfo.modules.includes(Subscriptions.Module_Portal) ? StepperTextPortal : StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: true,
    hideSkip,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit,
      modules: onboardingInfo.modules,
      prefilledEmployee
    },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_add-mechanics')}</>
}

const StepperTextPortal: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_add-mechanics_portal')}</>
}

export const AddMechanicsStep: React.FC<defaultStepFormProps & StepProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathAddMechanicsForm(basePath)} render={() => <OnboardingAddMechanicsForm {...rest} />} />
}
